import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { adminApi } from '../../services/adminApi';
import { mapsAPI } from '../../services/api';
import MapEditModal from './MapEditModal';
import {
    ArrowLeft, Mail, Calendar, Shield, Map as MapIcon,
    Eye, MapPin, Trash2, Edit, ExternalLink,
    CheckCircle, XCircle, Clock, Loader2
} from 'lucide-react';
import './AdminDashboard.css';
import './AdminUserDetails.css';

export default function AdminUserDetails() {
    const { userId } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [userData, setUserData] = useState(null);
    const [error, setError] = useState(null);
    const [showLimitModal, setShowLimitModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [editingMap, setEditingMap] = useState(null);
    const [deletingMapId, setDeletingMapId] = useState(null);

    const reload = async () => {
        const response = await adminApi.getUserDetails(userId);
        setUserData(response.data);
    };

    useEffect(() => {
        const loadUserDetails = async () => {
            try {
                setLoading(true);
                setError(null);
                const response = await adminApi.getUserDetails(userId);
                setUserData(response.data);
            } catch (err) {
                const msg = err?.response?.data?.message || err?.message || 'Unknown error';
                setError(msg);
                setTimeout(() => { alert(`Failed to load user details: ${msg}`); navigate('/admin'); }, 100);
            } finally {
                setLoading(false);
            }
        };
        if (userId) loadUserDetails();
    }, [userId, navigate]);

    const handleDeleteMap = async (mapId, mapTitle) => {
        if (!window.confirm(`Delete "${mapTitle}"? This will also delete all markers and cannot be undone.`)) return;
        try {
            setDeletingMapId(mapId);
            await mapsAPI.deleteMap(mapId);
            await reload();
        } catch (err) {
            alert(err?.response?.data?.message || err?.message || 'Failed to delete map');
        } finally {
            setDeletingMapId(null);
        }
    };

    const handleToggleAdmin = async () => {
        if (!userData) return;
        if (!window.confirm(`${userData.user.isAdmin ? 'Remove' : 'Grant'} admin privileges for ${userData.user.name}?`)) return;
        try {
            await adminApi.updateUser(userId, { isAdmin: !userData.user.isAdmin });
            await reload();
        } catch (err) {
            alert(err?.response?.data?.message || err?.message || 'Failed to update user');
        }
    };

    const handleToggleActive = async () => {
        if (!userData) return;
        if (!window.confirm(`${userData.user.isActive ? 'Deactivate' : 'Activate'} ${userData.user.name}?`)) return;
        try {
            await adminApi.updateUser(userId, { isActive: !userData.user.isActive });
            await reload();
        } catch (err) {
            alert(err?.response?.data?.message || err?.message || 'Failed to update user');
        }
    };

    const handleUpdateLimit = async (newLimit) => {
        try {
            await adminApi.updateUser(userId, { mapsLimit: parseInt(newLimit) });
            await reload();
        } catch (err) {
            throw new Error(err?.response?.data?.message || err?.message || 'Failed to update limit');
        }
    };

    if (loading) return (
        <div className="aud-loading">
            <Loader2 size={32} className="aud-spin" />
            <p>Loading user details…</p>
        </div>
    );

    if (error) return (
        <div className="aud-error-screen">
            <h2>Error Loading User</h2>
            <p>{error}</p>
            <button className="adm-btn adm-btn--primary" onClick={() => navigate('/admin')}>
                <ArrowLeft size={14} /> Back to Admin
            </button>
        </div>
    );

    if (!userData?.user) return (
        <div className="aud-error-screen">
            <h2>User Not Found</h2>
            <button className="adm-btn adm-btn--primary" onClick={() => navigate('/admin')}>
                <ArrowLeft size={14} /> Back to Admin
            </button>
        </div>
    );

    const { user, maps = [], stats = {} } = userData;
    const atLimit = (user.mapsCreatedCount || 0) >= (user.mapsLimit || 0);

    return (
        <div className="aud-root">
            {/* Page Header */}
            <div className="adm-page-header">
                <div className="adm-page-header-inner aud-page-header-row">
                    <div className="aud-header-left">
                        <button className="adm-btn adm-btn--ghost adm-btn--sm" onClick={() => navigate('/admin')}>
                            <ArrowLeft size={14} /> Back
                        </button>
                        <div>
                            <h1 className="adm-page-title">User Details</h1>
                            <p className="adm-page-subtitle">Managing {user.name}'s account</p>
                        </div>
                    </div>
                </div>
            </div>

            <div className="adm-container">
                {/* User Profile Card */}
                <div className="adm-card aud-profile-card">
                    <div className="aud-profile-left">
                        <img src={user.profilePicture} alt={user.name} className="aud-profile-avatar" />
                        <div className="aud-profile-info">
                            <h2 className="aud-profile-name">{user.name}</h2>
                            <div className="aud-profile-meta">
                                <span className="aud-meta-item"><Mail size={13} />{user.email}</span>
                                <span className="aud-meta-item"><Calendar size={13} />Joined {new Date(user.createdAt).toLocaleDateString()}</span>
                            </div>
                            <div className="aud-profile-badges">
                                <span className={`adm-badge ${user.isActive ? 'adm-badge--green' : 'adm-badge--red'}`}>
                                    {user.isActive ? 'Active' : 'Inactive'}
                                </span>
                                <span className={`adm-badge ${user.isAdmin ? 'adm-badge--purple' : 'adm-badge--gray'}`}>
                                    {user.isAdmin ? <><Shield size={11} /> Admin</> : 'User'}
                                </span>
                                <span className={`adm-badge ${atLimit ? 'adm-badge--red' : 'adm-badge--blue'}`}>
                                    {user.mapsCreatedCount || 0} / {user.mapsLimit || 0} Maps
                                </span>
                            </div>
                        </div>
                    </div>
                    <div className="aud-profile-actions">
                        <button className="adm-btn adm-btn--primary adm-btn--sm" onClick={() => setShowLimitModal(true)}>
                            Set Maps Limit
                        </button>
                        <button className="adm-btn adm-btn--ghost adm-btn--sm" onClick={handleToggleAdmin}>
                            {user.isAdmin ? 'Remove Admin' : 'Make Admin'}
                        </button>
                        <button
                            className={`adm-btn adm-btn--sm ${user.isActive ? 'adm-btn--danger' : 'adm-btn--primary'}`}
                            onClick={handleToggleActive}
                        >
                            {user.isActive ? 'Deactivate' : 'Activate'}
                        </button>
                    </div>
                </div>

                {/* Stats */}
                <div className="adm-stats-grid">
                    <StatCard icon={<MapIcon size={20} />} title="Total Maps" value={stats.totalMaps || 0} accent="#2B8EE8" />
                    <StatCard icon={<CheckCircle size={20} />} title="Active Maps" value={stats.activeMaps || 0} accent="#22C55E" />
                    <StatCard icon={<Eye size={20} />} title="Total Visits" value={stats.totalVisits || 0} accent="#8B5CF6" />
                    <StatCard icon={<MapPin size={20} />} title="Total Markers" value={stats.totalMarkers || 0} accent="#F59E0B" />
                </div>

                {/* Maps Table */}
                <div className="adm-card">
                    <div className="adm-card-header">
                        <h2 className="adm-card-title">Maps ({maps.length})</h2>
                    </div>

                    {maps.length === 0 ? (
                        <div className="aud-empty">
                            <MapIcon size={32} className="aud-empty-icon" />
                            <p>This user hasn't created any maps yet</p>
                        </div>
                    ) : (
                        <div className="adm-table-wrap">
                            <table className="adm-table">
                                <thead>
                                    <tr>
                                        <th>Map Details</th>
                                        <th>Status</th>
                                        <th>Dates</th>
                                        <th>Stats</th>
                                        <th className="adm-th-right">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {maps.map(map => (
                                        <tr key={map.id}>
                                            <td>
                                                <p className="aud-map-title">{map.title}</p>
                                                {map.description && <p className="aud-map-desc">{map.description}</p>}
                                                <p className="aud-map-id">ID: {map.mapId}</p>
                                            </td>
                                            <td>
                                                <div className="aud-badge-stack">
                                                    <span className={`adm-badge ${map.isActive ? 'adm-badge--green' : 'adm-badge--red'}`}>
                                                        {map.isActive ? <CheckCircle size={10} /> : <XCircle size={10} />}
                                                        {map.isActive ? 'Active' : 'Inactive'}
                                                    </span>
                                                    <span className={`adm-badge ${map.isPublic ? 'adm-badge--blue' : 'adm-badge--gray'}`}>
                                                        {map.isPublic ? 'Public' : 'Private'}
                                                    </span>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="aud-dates">
                                                    <span className="aud-date-item"><Calendar size={11} />Created {new Date(map.createdAt).toLocaleDateString()}</span>
                                                    <span className="aud-date-item"><Clock size={11} />Updated {new Date(map.updatedAt).toLocaleDateString()}</span>
                                                    <span className="aud-date-item aud-date-item--muted">
                                                        {new Date(map.startTime).toLocaleDateString()} — {new Date(map.endTime).toLocaleDateString()}
                                                    </span>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="aud-dates">
                                                    <span className="aud-date-item"><Eye size={11} />{map.visitCount || 0} visits</span>
                                                    <span className="aud-date-item"><MapPin size={11} />{map.markersCount || 0} markers</span>
                                                </div>
                                            </td>
                                            <td className="adm-td-right">
                                                <div className="aud-map-actions">
                                                    <a href={map.shareUrl} target="_blank" rel="noopener noreferrer" className="aud-action-btn aud-action-btn--blue" title="View Public Map">
                                                        <ExternalLink size={14} />
                                                    </a>
                                                    <button className="aud-action-btn aud-action-btn--green" title="Edit Map"
                                                        onClick={() => { setEditingMap(map); setShowEditModal(true); }}>
                                                        <Edit size={14} />
                                                    </button>
                                                    <button className="aud-action-btn aud-action-btn--red" title="Delete Map"
                                                        disabled={deletingMapId === map.id}
                                                        onClick={() => handleDeleteMap(map.id, map.title)}>
                                                        <Trash2 size={14} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            {showLimitModal && (
                <MapLimitModal user={user} onClose={() => setShowLimitModal(false)} onUpdate={async (limit) => { await handleUpdateLimit(limit); setShowLimitModal(false); }} />
            )}

            {showEditModal && editingMap && (
                <MapEditModal map={editingMap} onClose={() => { setShowEditModal(false); setEditingMap(null); }} onUpdate={reload} />
            )}
        </div>
    );
}

function StatCard({ icon, title, value, accent }) {
    return (
        <div className="adm-stat-card" style={{ '--accent': accent }}>
            <div className="adm-stat-icon">{icon}</div>
            <div className="adm-stat-body">
                <p className="adm-stat-label">{title}</p>
                <p className="adm-stat-value">{value}</p>
            </div>
        </div>
    );
}

function MapLimitModal({ user, onClose, onUpdate }) {
    const [limit, setLimit] = useState(user.mapsLimit || 0);
    const [loading, setLoading] = useState(false);
    const atLimit = (user.mapsCreatedCount || 0) >= (user.mapsLimit || 0);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await onUpdate(limit);
        } catch (error) {
            alert(error.message || 'Failed to update limit');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="adm-overlay" onClick={onClose}>
            <div className="adm-modal" onClick={e => e.stopPropagation()}>
                <div className="adm-modal-header">
                    <h3 className="adm-modal-title">Set Maps Limit — {user.name}</h3>
                    <button className="adm-icon-btn" onClick={onClose}>&times;</button>
                </div>
                <form onSubmit={handleSubmit} className="adm-modal-body">
                    <div className="adm-form-group">
                        <label className="adm-label">Maps Limit</label>
                        <input type="number" min="0" value={limit} onChange={e => setLimit(e.target.value)} className="adm-input" required />
                        <p className="adm-hint">Current usage: {user.mapsCreatedCount || 0} / {user.mapsLimit || 0} maps</p>
                        {atLimit && <p className="adm-hint adm-hint--red">User has reached their limit</p>}
                    </div>
                    <div className="adm-modal-footer">
                        <button type="button" className="adm-btn adm-btn--ghost" onClick={onClose} disabled={loading}>Cancel</button>
                        <button type="submit" className="adm-btn adm-btn--primary" disabled={loading}>
                            {loading ? 'Updating…' : 'Update Limit'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}